import React from 'react';
import { IAutocompleteEntry, CompletionType, IAutocompleteNeighbourhood } from 'utils/entities';
import { InitialSuggestion } from 'utils/initialSuggestionsConfig';
import { SuggestionItem } from './SuggestionItem';
import NoResult from './NoResult';
import LocationIcon from 'assets/svg/location-gallery.svg';
import { AddressAutocompleteSource } from '../types';
import { useLocale } from 'locale';
import { replaceString } from './utils';
import { GetItemPropsOptions } from 'downshift';
import { SuggestionTitleWrapper } from './styled';
import { Route, State as RouteState } from 'config/routes';
import { connect } from 'react-redux';
import { routeSelector } from 'store/state/selectors/router';
import { State } from 'store/state';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { INeighborhoodSummaryDocument } from 'store/sagas/apiService/types';
import { SimilarHoodItem } from 'screens/Investors/components/CompareModal/SimilarHoodItem';
import { SpacingBlock } from 'components/spacing-block';
import { searchDocEntriesSelector } from 'store/state/domainData/selectors';


interface SuggestionsProps {
  initialSuggestions?: InitialSuggestion[];
  suggestedAddresses: IAutocompleteEntry[];
  loading?: boolean;
  searchStr: string;
  source: AddressAutocompleteSource;
  optionsCount: number;
  showType?: boolean;
  highlightedIndex: number;
  itemPropsGetter: (options: GetItemPropsOptions<any>) => any;
  isOpen: boolean;
  children: (suggestions: React.ReactNode) => JSX.Element;
  itemIcon?: React.ReactNode;
  noResult?: JSX.Element;
  suggestionFooter?: React.ReactNode;
  suggestionsWithTitle?: boolean;
  route?: RouteState;
  similarHoods?: INeighborhoodSummaryDocument[];
  searchDocs?: IAutocompleteEntry[];
}

const highlighter = (str: string, i: number) => <span key={i}>{str}</span>;

const SuggestionsBase: React.FC<SuggestionsProps> = (props) => {
  const {
    initialSuggestions,
    suggestedAddresses,
    loading,
    searchStr,
    source,
    optionsCount,
    showType,
    highlightedIndex,
    itemPropsGetter,
    isOpen,
    children,
    itemIcon = <LocationIcon width={24} height={24} />,
    suggestionsWithTitle,
    route,
    similarHoods,
    searchDocs,
  } = props;

  const isInvestorSearch = route && route.name === Route.InvestorsSearchPage;
  const showSimilar = route && route.params && route.params.term && route.params.term.length === 1 && isInvestorSearch;

  const { t } = useLocale();
  const isDevice = useScreenBreakpoint() < 3;

  let content: React.ReactNode = null;

  if (!isOpen) {
    content = null;
  }
  else if (!suggestedAddresses.length && searchStr && !loading) {
    content = props.noResult ? <>{props.noResult}</> : <NoResult source={source} str={searchStr} />;
  }
  else if (suggestedAddresses.length) {
    const suggestionItems = suggestedAddresses.slice(0, optionsCount).map((address, index) => (
      <SuggestionItem
        key={address.docId + index}
        icon={itemIcon}
        isActive={index === highlightedIndex}
        downshiftItemProps={itemPropsGetter({
          key: address.docId,
          item: address.docId,
          index,
        })}
        label={replaceString(address.name, searchStr, highlighter)}
        secondaryLabel={showType ? t('autocomplete.completionType', { type: address.type }) : null}
      />
    ));
    content = (
      <>
        {suggestionItems}
        {props.suggestionFooter}
      </>
    );
  }
  else if (initialSuggestions && !searchStr) {
    const acc: React.ReactNodeArray = [];
    let parentIndex = 0;
    let index = 0;

    if (similarHoods && similarHoods.length && showSimilar && isInvestorSearch) {
      const doc = searchDocs && searchDocs.length && searchDocs[0];
      acc.push(<SuggestionTitleWrapper style={isInvestorSearch && showSimilar && !isDevice ? { paddingRight: 12 } : undefined} key={'similar_title' + parentIndex} isFirst={parentIndex === 0}>{t('investors.suggested.neightbourhoods.comapre.title', { name: (doc as IAutocompleteNeighbourhood).neighbourhood, city: doc.city })}</SuggestionTitleWrapper>);
      parentIndex++;
      similarHoods.forEach((el) => {
        const itemProps = itemPropsGetter({
          key: el.neighborhoodDoc.docId,
          item: el.neighborhoodDoc.docId,
          index,
        });
        acc.push(
          <SimilarHoodItem
            {...itemProps}
            key={`similar_item_${index}`}
            name={el.neighborhoodDoc.ref.name}
            labels={el.investorCard.labels}
          />
        );
        index++;
      });
      acc.push(<SpacingBlock mTop={2} key="space" />);
    }

    for (const { entries, title } of initialSuggestions) {
      if (suggestionsWithTitle || (isInvestorSearch && showSimilar && similarHoods && similarHoods.length)) {
        acc.push(<SuggestionTitleWrapper style={isInvestorSearch && showSimilar && !isDevice ? { paddingRight: 12 } : undefined} key={title + parentIndex} isFirst={parentIndex === 0}>{title}</SuggestionTitleWrapper>);
        parentIndex++;
      }
      for (const entry of entries) {
        const itemProps = itemPropsGetter({
          key: entry.docId,
          item: entry.docId,
          index,
        });
        acc.push(
          <SuggestionItem
            key={entry.docId + index}
            icon={itemIcon}
            isActive={index === highlightedIndex}
            downshiftItemProps={itemProps}
            label={entry.name}
            secondaryLabel={entry.type === CompletionType.Neighbourhood ? entry.borough : null}
          />
        );

        index++;
      }
    }

    content = acc;
  }

  return content ? children(content) : null;
};

const mapStateToProps = (state: State) => ({
  route: routeSelector(state),
  searchDocs: searchDocEntriesSelector(state),
});

export const Suggestions = connect(mapStateToProps)(SuggestionsBase);
